import Card from "../../components/shared/card";
import ThemedSpinner from "../../components/shared/spinner";
import Typography from "../../components/shared/typography";
// Inputs are rendered via FieldRenderer within RequestDetailsWizard
import { postContractSign } from "#/api/contracts.api";
import { ContractSignRequest } from "#/api/contracts.type";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SubmitButton } from "../../components/shared/buttons/submit-button";
import { useToast } from "../../hooks/ToastContext";
import { trackEvent } from "../../mixpanel";
import { HIPAA_DEF } from "../config/hipaa";
import { useRequestRecords } from "../context/RequestRecordsContext";
import { buildSignContractBody } from "../utils/contractPayload";
import { buildGroups, computeSectionStatuses } from "../utils/formSections";
import { generateHipaaPdf } from "../utils/pdf";
import InlineSteps from "./InlineSteps";
import ProgressOverview from "./ProgressOverview";
import ProviderSummaryCard from "./ProviderSummaryCard";
import RequestDetailsWizard from "./RequestDetailsWizard";
import SignatureCanvas from "./SignatureCanvas";
import StepCard from "./StepCard";

export default function RequestRecordsLayout() {
  const { data, isLoading, error } = useRequestRecords();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Simple request details form state with autosave to localStorage
  const storageKey = useMemo(
    () => (slug ? `request-records:${slug}` : undefined),
    [slug]
  );
  const providerDefaults = useMemo(() => {
    const displayName = data
      ? data.type === "provider"
        ? (data.record as any).name ||
          `${(data.record as any).first_name ?? ""} ${
            (data.record as any).last_name ?? ""
          }`.trim()
        : (data.record as any).name
      : "";
    const recAddr = (data as any)?.record?.address || {};
    const apiAddr = (data as any)?.address || {};
    const line1 =
      (apiAddr.line1 as string) ||
      (apiAddr.street as string) ||
      (recAddr.line1 as string) ||
      (recAddr.street as string) ||
      "";
    const line2 = (apiAddr.line2 as string) || (recAddr.line2 as string) || "";
    const city =
      ((data?.record as any)?.city as string) ||
      (apiAddr.city as string) ||
      (recAddr.city as string) ||
      "";
    const state =
      ((data?.record as any)?.state as string) ||
      (apiAddr.state as string) ||
      (recAddr.state as string) ||
      "";
    const zip =
      (apiAddr.postal_code as string) ||
      ((data?.record as any)?.zip as string) ||
      (recAddr.zip as string) ||
      "";
    return {
      provider_name: displayName || "",
      provider_address_line1: line1,
      provider_address_line2: line2,
      provider_address_city: city,
      provider_address_state: state,
      provider_address_zip: zip,
      recipient_name: "Medical Records Department",
      recipient_org: "",
      dates_all: true,
      info_all: true,
      purpose_treatment: true,
      purpose_insurance: false,
      purpose_legal: false,
      purpose_personal: false,
    } as Record<string, any>;
  }, [data]);

  const initialForm = useMemo(() => {
    const base: Record<string, any> = {};
    HIPAA_DEF.fields.forEach((f) => {
      switch (f.type) {
        case "checkbox":
          base[f.name] = false;
          break;
        case "date":
          base[f.name] = "";
          break;
        case "initials":
        case "email":
        case "text":
        default:
          base[f.name] = "";
      }
    });
    // sensible defaults
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    base.expiration_date = oneYearFromNow.toISOString().slice(0, 10);
    base.ack_revocation = true;
    base.ack_redisclosure = true;
    base.ack_electronic_delivery = true;
    base.ack_conditions = true;
    base.representative_is_patient = true; // default to patient
    return base;
  }, []);

  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    trigger,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<Record<string, any>>({
    defaultValues: initialForm,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
    "idle"
  );
  const [chunkIndex, setChunkIndex] = useState(0);
  const [phase, setPhase] = useState<1 | 2 | 3>(1);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [identityVerifiedTimestamp, setIdentityVerifiedTimestamp] = useState<
    string | undefined
  >(undefined);
  const [identityVerificationType, setIdentityVerificationType] = useState<
    string | undefined
  >(undefined);
  const [sessionId, setSessionId] = useState<string>("");
  const [clientTimezone, setClientTimezone] = useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  });
  const [signatureCompleted, setSignatureCompleted] = useState(false);
  const isDev = process.env.NODE_ENV !== "production";
  const [isSending, setIsSending] = useState(false);
  const lastTrackedStep = useRef<number | null>(null);
  const startTracked = useRef<boolean>(false);
  const stepCompletionRef = useRef({
    details: false,
    identity: false,
    signature: false,
  });

  // Load saved draft
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed === "object" &&
          (parsed.form || parsed.step !== undefined)
        ) {
          reset({ ...initialForm, ...(parsed.form || {}) });
          if (typeof parsed.step === "number") setChunkIndex(parsed.step);
          if (parsed.status && typeof parsed.status === "object") {
            setIdentityVerified(Boolean(parsed.status.identityVerified));
            setSignatureCompleted(Boolean(parsed.status.signatureCompleted));
            if (parsed.status.identityVerifiedTimestamp)
              setIdentityVerifiedTimestamp(
                String(parsed.status.identityVerifiedTimestamp)
              );
            if (parsed.status.identityVerificationType)
              setIdentityVerificationType(
                String(parsed.status.identityVerificationType)
              );
            if (parsed.status.sessionId)
              setSessionId(String(parsed.status.sessionId));
            if (parsed.status.clientTimezone)
              setClientTimezone(String(parsed.status.clientTimezone));
            if (
              parsed.status.phase &&
              [1, 2, 3].includes(parsed.status.phase)
            ) {
              setPhase(parsed.status.phase as 1 | 2 | 3);
            }
          }
        } else if (parsed && typeof parsed === "object") {
          reset({ ...initialForm, ...parsed });
        }
      }
    } catch {}
  }, [storageKey, reset, initialForm]);

  // Apply provider defaults when data loads (without overriding saved or typed values)
  useEffect(() => {
    if (!data) return;
    const current = getValues();
    const next: Record<string, any> = { ...current };
    Object.entries(providerDefaults).forEach(([k, v]) => {
      if (next[k] === undefined || next[k] === "" || next[k] === false) {
        next[k] = v;
      }
    });
    reset(next, { keepDefaultValues: true });
  }, [providerDefaults, data, getValues, reset]);

  // Autosave draft (debounced)
  useEffect(() => {
    if (!storageKey) return;
    const subscription = watch((values) => {
      setSaveState("saving");
      const t = setTimeout(() => {
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify({
              form: values,
              step: chunkIndex,
              status: {
                identityVerified,
                signatureCompleted,
                phase,
                identityVerifiedTimestamp,
                identityVerificationType,
                sessionId,
                clientTimezone,
              },
            })
          );
          setSaveState("saved");
        } catch {
          setSaveState("idle");
        }
      }, 400);
      return () => clearTimeout(t);
    });
    return () => subscription.unsubscribe();
  }, [
    watch,
    storageKey,
    chunkIndex,
    identityVerified,
    signatureCompleted,
    phase,
    identityVerifiedTimestamp,
    identityVerificationType,
    sessionId,
    clientTimezone,
  ]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const values = getValues();
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          form: values,
          step: chunkIndex,
          status: {
            identityVerified,
            signatureCompleted,
            phase,
            identityVerifiedTimestamp,
            identityVerificationType,
            sessionId,
            clientTimezone,
          },
        })
      );
    } catch {}
  }, [
    chunkIndex,
    storageKey,
    getValues,
    identityVerified,
    signatureCompleted,
    phase,
    identityVerifiedTimestamp,
    identityVerificationType,
    sessionId,
    clientTimezone,
  ]);

  // Ensure we have a sessionId and timezone early
  useEffect(() => {
    if (!sessionId) {
      try {
        const id =
          slug ||
          (typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : String(Date.now()));
        setSessionId(id);
      } catch {
        setSessionId(slug || String(Date.now()));
      }
    }
  }, [sessionId, slug]);

  // When identityVerified flips to true and timestamp/type are missing, set them
  useEffect(() => {
    if (identityVerified && !identityVerifiedTimestamp) {
      setIdentityVerifiedTimestamp(new Date().toISOString());
    }
    if (identityVerified && !identityVerificationType) {
      setIdentityVerificationType("license");
    }
  }, [identityVerified, identityVerifiedTimestamp, identityVerificationType]);

  const requiredFields: string[] = [
    "patient_full_name",
    "patient_dob",
    "provider_name",
    "recipient_name",
    "ack_revocation",
    "ack_redisclosure",
    "ack_conditions",
  ];
  const formValues = watch();
  const doneCount = requiredFields.filter((k) => {
    const v = formValues[k];
    return typeof v === "boolean" ? v : String(v || "").trim().length > 0;
  }).length;
  const detailsCompleted = doneCount >= requiredFields.length;
  const formRatio = doneCount / requiredFields.length;
  const overallProgressPct = Math.round(
    ((formRatio + (identityVerified ? 1 : 0) + (signatureCompleted ? 1 : 0)) /
      3) *
      100
  );
  const currentStep: number = phase;
  const canGoVerify = detailsCompleted;
  const canGoSign = canGoVerify && identityVerified;
  const allStepsComplete =
    detailsCompleted && identityVerified && signatureCompleted;

  const groups = useMemo(() => buildGroups(formValues), [formValues]);

  // Fire a one-time start event for step 0
  useEffect(() => {
    if (startTracked.current) return;
    trackEvent("Medical Records Start", {
      stepNumber: 0,
      stepName: "start",
      slug,
      sessionId,
    });
    startTracked.current = true;
  }, [sessionId, slug]);

  useEffect(() => {
    if (lastTrackedStep.current === currentStep) return;
    trackEvent("Medical Records Step Viewed", {
      stepNumber: currentStep,
      stepName:
        currentStep === 1
          ? "request-details"
          : currentStep === 2
          ? "identity-verification"
          : "signature",
      slug,
      sessionId,
    });
    lastTrackedStep.current = currentStep;
  }, [currentStep, sessionId, slug]);

  useEffect(() => {
    const completion = stepCompletionRef.current;

    if (detailsCompleted && !completion.details) {
      trackEvent("Medical Records Step Completed", {
        stepNumber: 1,
        stepName: "request-details",
        slug,
        sessionId,
      });
      completion.details = true;
    }

    if (identityVerified && !completion.identity) {
      trackEvent("Medical Records Step Completed", {
        stepNumber: 2,
        stepName: "identity-verification",
        slug,
        sessionId,
      });
      completion.identity = true;
    }

    if (signatureCompleted && !completion.signature) {
      trackEvent("Medical Records Step Completed", {
        stepNumber: 3,
        stepName: "signature",
        slug,
        sessionId,
      });
      completion.signature = true;
    }
  }, [detailsCompleted, identityVerified, signatureCompleted, sessionId, slug]);

  useEffect(() => {
    if (chunkIndex >= groups.length) {
      setChunkIndex(Math.max(0, groups.length - 1));
    }
  }, [groups.length, chunkIndex]);

  // Ensure expiration_date has a default one year from today when empty
  useEffect(() => {
    const v = getValues();
    if (!v.expiration_date) {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      const iso = d.toISOString().slice(0, 10);
      setValue("expiration_date", iso, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [getValues, setValue]);

  const goToIdentityVerify = () => {
    const redirect = `${location.pathname}${location.search}${location.hash}`;
    const emailParam = formValues?.patient_email
      ? `&email=${encodeURIComponent(String(formValues.patient_email))}`
      : "";
    const identityUrl = `/identity/verify?redirect=${encodeURIComponent(
      redirect
    )}${emailParam}`;
    trackEvent("Medical Records Identity Verification Started", {
      slug,
      ...(sessionId ? { sessionId } : {}),
    });
    navigate(identityUrl);
  };

  // Field rendering moved to FieldRenderer component

  const sectionStatuses = useMemo(
    () => computeSectionStatuses(formValues),
    [formValues]
  );

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <ThemedSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Typography variant="h4" className="text-terracotta">
          Couldn’t load provider
        </Typography>
      </div>
    );
  }

  const { type, record, address, medical_specialty } = data;
  const displayName =
    type === "provider"
      ? (record as any).name ||
        `${(record as any).first_name ?? ""} ${
          (record as any).last_name ?? ""
        }`.trim()
      : (record as any).name;
  const city = (record as any).city || address?.city;
  const state = (record as any).state || address?.state;
  const specialty = (
    medical_specialty?.name ||
    (record as any).specialization ||
    (record as any).specialty
  )?.toString();

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mb-6">
        <Typography variant="h3" className="text-brown-900">
          Request Medical Records
        </Typography>
      </div>

      {/* Step cards at the top */}
      {/* Compact inline steps on small screens */}
      <div className="md:hidden mb-4">
        <InlineSteps
          currentStep={currentStep}
          doneStep1={detailsCompleted}
          doneStep2={identityVerified}
          doneStep3={signatureCompleted}
          canGoVerify={canGoVerify}
          canGoSign={canGoSign || isDev}
          onGoDetails={() => setPhase(1)}
          onGoVerify={() => {
            setPhase(2);
            goToIdentityVerify();
          }}
          onGoSign={() => {
            if (canGoSign || isDev) setPhase(3);
          }}
        />
      </div>
      {/* Full cards on md+ screens */}
      <div className="hidden md:grid grid-cols-3 gap-4 mb-8">
        <StepCard
          step={1}
          title="Request Details"
          description="Provide the info needed to process your request."
          active={currentStep === 1}
          completed={detailsCompleted}
          onClick={() => setPhase(1)}
        />
        <StepCard
          step={2}
          title="Verify your identity"
          description="Confirm who you are to keep your information secure."
          active={currentStep === 2}
          completed={identityVerified}
          onClick={
            canGoVerify
              ? () => {
                  setPhase(2);
                  goToIdentityVerify();
                }
              : undefined
          }
          disabled={!canGoVerify}
        />
        <StepCard
          step={3}
          title="Sign form & send"
          description="Electronically sign and submit your request for records."
          active={currentStep === 3}
          completed={signatureCompleted}
          onClick={canGoSign || isDev ? () => setPhase(3) : undefined}
          disabled={!canGoSign && !isDev}
        />
      </div>

      <ProviderSummaryCard
        displayName={displayName}
        specialty={specialty}
        city={city}
        state={state}
      />

      <ProgressOverview
        overallProgressPct={overallProgressPct}
        saveState={saveState}
        sectionStatuses={sectionStatuses as any}
      />

      {/* Request Details mini-wizard */}
      <RequestDetailsWizard
        groups={groups}
        chunkIndex={chunkIndex}
        setChunkIndex={setChunkIndex}
        control={control}
        errors={errors as any}
        formValues={formValues}
        trigger={trigger as any}
        canContinue={detailsCompleted}
        onContinueVerify={() => {
          setPhase(2);
          goToIdentityVerify();
        }}
        showToast={showToast}
      />

      {/* Signature step UI */}
      {currentStep === 3 && (
        <Card className="mb-8" variant="beige" elevation={1}>
          <Card.Header variant="beige">
            <Typography variant="h5" className="text-brown-900">
              Sign Authorization
            </Typography>
            <Typography variant="body2" className="text-brown-700 mt-1">
              Please sign below to authorize release of your medical records.
            </Typography>
          </Card.Header>
          <Card.Body>
            <SignatureCanvas
              onChange={(dataUrl) => {
                setValue("signature_data_url", dataUrl || "", {
                  shouldDirty: true,
                  shouldValidate: false,
                });
              }}
            />
            <div className="mt-3 flex items-center justify-between">
              <Typography variant="caption" className="text-brown-600">
                Draw your signature inside the box.
              </Typography>
              <SubmitButton
                color="terracotta"
                size="sm"
                onClick={() => {
                  const hasSignature = Boolean(getValues().signature_data_url);
                  if (!hasSignature) {
                    showToast("Please provide your signature", "error");
                    return;
                  }
                  setValue("signature_timestamp", new Date().toISOString(), {
                    shouldDirty: true,
                    shouldValidate: false,
                  });
                  setSignatureCompleted(true);
                  showToast("Signature saved", "success");
                }}
              >
                Save Signature
              </SubmitButton>
            </div>
            {/* Subtle download option for users who prefer their own submission */}
            <div className="mt-3 flex items-center justify-start">
              <SubmitButton
                color="beige"
                size="sm"
                hideArrow
                className="opacity-70 hover:opacity-100"
                onClick={async () => {
                  try {
                    // Derive provider phone/fax if available
                    let provider_phone: string | undefined;
                    let provider_fax: string | undefined;
                    try {
                      const rec: any = (data as any)?.record || {};
                      const details: any[] = Array.isArray(rec.contact_details)
                        ? rec.contact_details
                        : [];
                      provider_phone =
                        rec.phone ||
                        details.find((d) => d?.phone)?.phone ||
                        details.find((d) =>
                          String(d?.type || d?.label || "")
                            .toLowerCase()
                            .includes("phone")
                        )?.value;
                      provider_fax =
                        rec.fax ||
                        details.find((d) => d?.fax)?.fax ||
                        details.find((d) =>
                          String(d?.type || d?.label || "")
                            .toLowerCase()
                            .includes("fax")
                        )?.value;
                    } catch {}

                    const augmented = {
                      ...getValues(),
                      provider_phone,
                      provider_fax,
                    } as any;

                    const blob = await generateHipaaPdf(
                      augmented,
                      getValues().signature_data_url
                    );
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `hipaa-authorization-${slug || "request"}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    showToast("Downloaded PDF copy", "success");
                  } catch (e) {
                    showToast("Failed to generate PDF", "error");
                  }
                }}
              >
                Download PDF copy
              </SubmitButton>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Final actions */}
      <div className="flex items-center justify-between">
        {/* Left: subtle download option already provided above, keep a second link here if desired */}
        <div />
        <SubmitButton
          color="terracotta"
          size="md"
          isDisabled={!allStepsComplete}
          isLoading={isSending}
          onClick={handleSubmit(async (values) => {
            // Enforce completion
            const ok = await trigger();
            if (!ok || !allStepsComplete) {
              showToast("Please complete all steps first", "error");
              return;
            }
            try {
              setIsSending(true);
              const payload = await buildSignContractBody({
                values,
                slug,
                identityVerifiedTimestamp: identityVerified
                  ? identityVerifiedTimestamp || new Date().toISOString()
                  : undefined,
                identityVerificationType: identityVerified
                  ? identityVerificationType || "stripe-identity"
                  : undefined,
              });
              await postContractSign(payload as ContractSignRequest);
              trackEvent("Medical Records Request Sent", {
                slug,
                ...(sessionId ? { sessionId } : {}),
              });
              showToast("Request sent successfully", "success");
            } catch (e) {
              trackEvent("Medical Records Request Failed", {
                slug,
                ...(sessionId ? { sessionId } : {}),
              });
              showToast("Failed to send request", "error");
            } finally {
              setIsSending(false);
            }
          })}
        >
          Send Request
        </SubmitButton>
      </div>

      {/* Steps moved to top; kept only once */}
    </div>
  );
}
